/**
 * Ambassadors Page - WoodAlps Brand Ambassadors Showcase
 * 
 * This page showcases WoodAlps brand ambassadors through dedicated sections
 * for each ambassador, following the same design pattern as other sections
 * on the website. Each ambassador gets a full-width section with their
 * story, photos, and social links.
 * 
 * Features:
 * - Section-based layout for each ambassador
 * - Consistent with existing site design patterns
 * - Responsive design for all device sizes
 * - Rich content support for ambassador stories
 * - Social media integration
 * - Filter functionality by bike category
 * 
 * @description Brand ambassadors showcase page with section-based layout
 */

import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import AmbassadorSection from '../components/sections/AmbassadorSection';
import { getAmbassadors } from '../src/app/studio/lib/sanity';
import { urlForImage } from '../src/app/studio/lib/image';
import dynamic from 'next/dynamic';
import { useTranslation } from '../src/hooks/useTranslation'

// Dynamically import the Leaflet ambassadors map component to avoid SSR issues
const LeafletAmbassadorsMap = dynamic(() => import('../components/ui/LeafletAmbassadorsMap'), {
  ssr: false,
  loading: () => <div className="w-full h-[500px] mx-auto rounded-xl bg-gray-200 flex items-center justify-center">Loading map...</div>
});

// Fake ambassador data for development
const ambassadorsData = [
  {
    id: 'sarah-johnson',
    name: 'Sarah Johnson',
    title: 'Professional Road Cyclist',
    location: 'Chamonix, France',
    yearsWithWoodAlps: 3,
    bikeCategory: 'Road',
    quote: 'Wood is life. Every pedal stroke connects me deeper to nature and to the craft that created my bike.',
    portraitImage: {
      src: '/images/grootybike.jpg', // Using existing image as placeholder
      alt: 'Sarah Johnson portrait'
    },
    actionImages: [
      {
        src: '/images/road-hero-grooty.jpg',
        alt: 'Sarah riding through Alpine roads'
      },
      {
        src: '/images/road-description-detail.jpg',
        alt: 'Close-up of Sarah\'s custom WoodAlps bike'
      },
      {
        src: '/images/road-description-Cockpit.jpg',
        alt: 'Sarah adjusting her bike setup'
      }
    ],
    story: `When I first laid eyes on a WoodAlps bike, I knew I was looking at something extraordinary. 
    The craftsmanship, the attention to detail, the way the wood grain flows through the frame - 
    it's not just a bike, it's a piece of art.

    I've been racing professionally for over 8 years, and I've ridden bikes made from every material 
    imaginable. But nothing compares to the ride quality of wood. The natural vibration dampening 
    creates a smooth, connected feeling with the road that you simply can't get from carbon or aluminum.

    
   `,
    email: 'sarah@sarahjohnsoncycling.com',
    phone: '+33 6 12 34 56 78',
    address: 'Chamonix Valley, French Alps',
    socialLinks: {
      instagram: '@sarahjohnson_cycling',
      facebook: 'sarahjohnsoncycling',
      strava: '12345678',
      website: 'www.sarahjohnsoncycling.com'
    },
    bikeName: 'GROOTY'
  },
  {
    id: 'marco-adventures',
    name: 'Marco Adventuris',
    title: 'Bikepacking Explorer',
    location: 'Dolomites, Italy',
    yearsWithWoodAlps: 2,
    bikeCategory: 'Gravel',
    quote: 'Every trail tells a story. With my BRANCHY, I write new chapters of adventure every day.',
    portraitImage: {
      src: '/images/DolomitesMTBj.jpg', // Using existing image as placeholder
      alt: 'Marco Adventuris portrait'
    },
    actionImages: [
      {
        src: '/images/gravel-hero-BikePacking.jpeg',
        alt: 'Marco bikepacking through mountain trails'
      },
      {
        src: '/images/gravel-description1.jpg',
        alt: 'Marco setting up camp with his BRANCHY'
      },
      {
        src: '/images/gravel-description2.jpg',
        alt: 'Sunrise ride through Alpine meadows'
      }
    ],
    story: `Adventure calls, and I answer with my WoodAlps BRANCHY. For the past two years, 
    this incredible machine has been my companion on some of the most remote and challenging 
    trails across Europe and beyond.

    What drew me to WoodAlps wasn't just the unique aesthetic - though the organic curves and 
    natural wood finish never fail to impress fellow travelers. It was the promise of a bike 
    that could handle anything I could throw at it while maintaining the comfort needed for 
    multi-day expeditions.

    From the rocky paths of the Dolomites to the sandy tracks of Morocco, my BRANCHY has 
    proven itself time and again. The natural shock absorption of the wooden frame means 
    I arrive at camp each evening less fatigued and more ready to enjoy the experience.

    The attention to detail in the design - the integrated cable routing, the multiple 
    mounting points for gear, the versatile geometry - shows that WoodAlps truly understands 
    what adventure riders need. But beyond the technical specifications, there's something 
    magical about riding a bike that was grown in a forest before becoming part of my journey.

    Every scratch and mark on the frame tells a story of the places we've been together. 
    It's not just transportation; it's a trusted partner in exploration.`,
    email: 'marco@marcoadventures.com',
    phone: '+39 334 567 8901',
    address: 'Val di Fassa, Dolomites, Italy',
    socialLinks: {
      instagram: '@marco_adventures',
      facebook: 'marcoadventuris',
      strava: '87654321',
      website: 'www.marcoadventures.com'
    },
    bikeName: 'BRANCHY'
  }
];

export default function Ambassadors({ ambassadors }) {
  const contentRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [activeFilter, setActiveFilter] = useState('All');
  const [filteredAmbassadors, setFilteredAmbassadors] = useState(ambassadors || ambassadorsData);
  const { t } = useTranslation();
  // Get header height on mount and window resize
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        // Get the full height including navigation
        setHeaderHeight(header.offsetHeight + 40); // Adding 40px for the navigation bar below
      }
    };

    // Initial calculation
    updateHeaderHeight();
    
    // Update on resize
    window.addEventListener('resize', updateHeaderHeight);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Custom smooth scroll function with consistent animation
  const smoothScrollTo = (targetPosition, duration = 500) => {
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    let startTime = null;

    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function - ease-in-out cubic
      const ease = t => t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1;
      
      window.scrollTo(0, startPosition + distance * ease(progress));
      
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    }
    
    requestAnimationFrame(animation);
  };

  const scrollToContent = () => {
    if (contentRef.current) {
      const sectionPosition = contentRef.current.getBoundingClientRect().top + window.scrollY;
      
      // Use exact headerHeight without additional offset
      smoothScrollTo(sectionPosition - headerHeight);
    }
  };

  // Filter functionality
  useEffect(() => {
    const dataToFilter = ambassadors || ambassadorsData;
    if (activeFilter === 'All') {
      setFilteredAmbassadors(dataToFilter);
    } else {
      setFilteredAmbassadors(dataToFilter.filter(ambassador => ambassador.bikeCategory === activeFilter));
    }
  }, [activeFilter, ambassadors]);

  const handleFilterChange = (category) => {
    setActiveFilter(category);
  };


  return (
    <>
      <Head>
        <title>{t('ambassadors.meta_title')}</title>
        <meta
          name="description"
          content={t('ambassadors.meta_description')}
        />
        <link 
          rel="stylesheet" 
          href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
          integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
          crossOrigin=""
        />
        <style>{`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-20px);
            }
            60% {
              transform: translateY(-10px);
            }
          }
        `}</style>
      </Head>

      {/* Hero Section - Mobile responsive */}
      <section className="relative h-screen sm:h-screen w-full overflow-hidden min-h-[600px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/images/Hero-ambassadeur.png"
            alt="WoodAlps   Ambassadors"
            fill
            className="object-cover"
            priority
          />
        </div>
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50"></div>

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 md:px-8">
          <h1 className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 font-neuropol leading-tight">
            {t('ambassadors.title')}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-xs sm:max-w-2xl md:max-w-3xl leading-relaxed px-2 sm:px-0">
            {t('ambassadors.subtitleHero_part_1')}{t('ambassadors.subtitleHero_part_2')}
          </p>
          
          {/* Animated Down Arrow */}
          <button 
            onClick={scrollToContent} 
            className="absolute bottom-6 sm:bottom-8 md:bottom-10 left-1/2 transform -translate-x-1/2 text-white cursor-pointer focus:outline-none"
            aria-label="Scroll down"
          >
            <p className="text-sm sm:text-base md:text-lg mb-1 sm:mb-2 text-white">{t('ambassadors.button_hero')}</p>
            <ChevronDown 
              size={24}
              className="mx-auto animate-bounce text-white sm:w-8 sm:h-8" 
              style={{ 
                animation: 'bounce 1s infinite',
                opacity: 0.8,
              }} 
            />
          </button>
        </div>
      </section>

      {/* Main Content */}
      <div ref={contentRef} className="bg-white">
        {/* Professional Message Section with Map and Button */}
        <section className="py-12">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center mt-12">
              <h2 className="text-l md:text-2xl font-bold text-deep-black mb-4 font-neuropol">
                {t('ambassadors.experience_woodalps_through_our_ambassadors')}
              </h2>
              <p className="text-m md:text-m text-gray-700 leading-relaxed mb-6 font-roboto text-justify">
                {t('ambassadors.experience_woodalps_through_our_ambassadors_description')}
              </p>
              <p className="text-base md:text-m text-gray-600 font-roboto text-justify mb-8">
                {t('ambassadors.experience_woodalps_through_our_ambassadors_description_2')}
              </p>
              
              {/* Ambassador Map */}
              <div className="mb-8 mt-20">
                <h3 className="text-lg md:text-xl font-bold text-deep-black mb-6 font-neuropol">
                  {t('ambassadors.find_our_ambassadors_across_europe')}
                </h3>
                <div className="max-w-5xl mx-auto">
                  <LeafletAmbassadorsMap ambassadors={filteredAmbassadors} />
                </div>
              </div>
              
              {/* Contact Ambassador Button */}
              <div className="mb-8 mt-20">
                <Link 
                  href="/contact"
                  className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-accent-color transition-all duration-300 font-neuropol text-sm font-bold shadow-lg hover:shadow-xl"
                >
                  {t('ambassadors.request_button')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="bg-white py-8 mt-12">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { value: 'All', translationKey: 'filter_all' },
                { value: 'Road', translationKey: 'filter_road' },
                { value: 'Gravel', translationKey: 'filter_gravel' },
                { value: 'MTB', translationKey: 'filter_mtb' }
              ].map(({ value, translationKey }) => (
                <button
                  key={value}
                  onClick={() => handleFilterChange(value)}
                  className={`px-6 py-2 rounded-md font-neuropol text-sm transition-colors shadow-md ${
                    activeFilter === value
                      ? 'bg-accent-color text-white hover:bg-accent-color/90'
                      : 'bg-gray-100 text-deep-black border border-gray-900 hover:bg-accent-color hover:text-white hover:border-accent-color'
                  }`}
                >
                  {t(`ambassadors.${translationKey}`)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Ambassador Sections */}
        {filteredAmbassadors.map((ambassador, index) => {
          // Transform Sanity data to match component expectations
          const transformedData = {
            id: ambassador._id || ambassador.id,
            name: ambassador.fullName || ambassador.name,
            title: ambassador.title,
            location: ambassador.location,
            yearsWithWoodAlps: ambassador.joiningDate ? 
              Math.floor((new Date() - new Date(ambassador.joiningDate)) / (365.25 * 24 * 60 * 60 * 1000)) :
              ambassador.yearsWithWoodAlps,
            bikeCategory: ambassador.bikeCategory,
            bikeSize: ambassador.bikeSize,
            quote: ambassador.quote,
            portraitImage: ambassador.portraitImage ? {
              src: urlForImage(ambassador.portraitImage),
              alt: ambassador.portraitImage.alt || `${ambassador.fullName || ambassador.name} portrait`
            } : ambassador.portraitImage,
            actionImages: ambassador.actionImages ? 
              ambassador.actionImages.map(img => ({
                src: urlForImage(img),
                alt: img.alt || `${ambassador.fullName || ambassador.name} action photo`
              })) : ambassador.actionImages,
            story: Array.isArray(ambassador.story) ? 
              ambassador.story.map(block => 
                block.children?.map(child => child.text).join('')
              ).join('\n\n') : (ambassador.story || ''),
            email: ambassador.contactInfo?.email || ambassador.email,
            phone: ambassador.contactInfo?.phone || ambassador.phone,
            address: ambassador.contactInfo?.address || ambassador.address,
            socialLinks: ambassador.socialLinks,
            bikeName: ambassador.bikeName
          };

          return (
            <AmbassadorSection 
              key={transformedData.id}
              data={transformedData}
              reverse={index % 2 !== 0}
              bgColor={index % 2 === 0 ? 'bg-white' : 'bg-wood-brown/20'}
            />
          );
        })}
      </div>


    </>
  );
}

// Define layout for this page
Ambassadors.getLayout = function getLayout(page) {
  return <Layout transparentHeader={true}>{page}</Layout>;
};

export async function getStaticProps({ preview = false, locale = 'en' }) {
  try {
    const ambassadors = await getAmbassadors(preview, locale);
    
    console.log("Sanity ambassadors data:", 
      ambassadors ? 
      `Found ${ambassadors.length} ambassadors` : 
      'No ambassadors returned'
    );
    
    return {
      props: {
        ambassadors: ambassadors || [],
        preview,
      },
      // Regenerate on every request in preview, otherwise once per day
      revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400,
    };
  } catch (error) {
    console.error('Error in getStaticProps for ambassadors page:', error);
    return {
      props: {
        ambassadors: [],
        preview: false,
      },
      revalidate: 86400,
    };
  }
}
